import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MatSidenav } from '@angular/material/sidenav';
import { AuthService } from '../../auth/auth.service';
import { LocalStorageService } from '../local-storage.service';
import { ChatService } from '../chat.service';
import { SwirlService, ChatSession } from '../swirl.service';
import { CookieConsentService } from 'src/app/shared/cookie-consent.service';
import { exportChatPdf } from 'src/app/shared/chat-export.service';
import {ConfigService} from "../config.service";


@Component({
    selector: 'app-chat-spyglass',
    templateUrl: './spyglass-chat.component.html',
    styleUrls: ['./spyglass-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class SpyglassChatComponent implements OnInit {

  hasFacets: boolean = false;
  type: string = '';
  mode: string = localStorage.getItem('isDark') || 'Light';
  backgroundImage: string = 'url(/api/swirl/sapi/branding/?target=' + this.mode.toLowerCase() + ')';
  isLoading: boolean = false;
  sources = [
    { value: 'default', name: 'All Sources', connector: 'default' }
  ];
  isEnabledCharts: boolean = false;
  isConversationStarted: boolean = false;
  messages: any[] = [];
  chatId: any = null;
  isAIProviderExists: boolean = true;
  isAIProviderLoaded: boolean = false;
  chatSessions: ChatSession[] = [];
  logoUrl: string = '';
  useChatLogoStyle: boolean = false;
  chatHistoryEnabled: boolean = true;

  private cookieConsentInstance: any;

  @ViewChild('history') historySidenav!: MatSidenav;

  constructor(
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    public chatService: ChatService,
    private router: Router,
    private authService: AuthService,
    private swirlService: SwirlService,
    private titleService: Title,
    private cookieConsentService: CookieConsentService,
    private configService: ConfigService
  ) {
    this.isEnabledCharts = false;
    this.isConversationStarted = false;
    this.isAIProviderExists = this.authService.getAIProviderStatus();
    this.titleService.setTitle('SWIRL Assistant');
  }

  private updateUrlWithChatId(chatId: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        chat_id: chatId
      },
      queryParamsHandling: 'merge', // merge with other query params
    });
  }

  ngOnInit(): void {
    this.configService.getConfig('default').subscribe(configResponse => {
      if (configResponse) {
        if (configResponse.chatHistoryEnabled !== undefined) {
          this.chatHistoryEnabled = configResponse.chatHistoryEnabled;
        }
      }
    });

    // Initialize cookie consent banner
    this.cookieConsentService.initCookieConsent();

    // Get custom Chat Logo from BrandingConfig, if available
    this.swirlService.getBrandingValues('config').subscribe((branding) => {
      console.log('Branding config received:', branding);

      this.useChatLogoStyle = !!branding.chat_logo;
      this.logoUrl = this.useChatLogoStyle
        ? '/api/swirl/sapi/branding/?target=chat'
        : '/api/swirl/sapi/branding/?target=light';
    });

    // Subscribe to AIProvider status; update loaded flag once done
    this.swirlService.getIsAIProviderExistsStatus().subscribe((data) => {
      this.isAIProviderLoaded = true; // provider check complete
      if (data && data.status) {
        const exists = data.status === 'True';
        this.authService.setAIProviderStatus(exists);
        this.isAIProviderExists = exists;
      } else {
        this.isAIProviderExists = false;
      }
    });

    // Handle Dark mode toggling
    if (!document.body.classList.contains("dark-theme") && this.mode === 'Dark') {
      document.body.classList.toggle("dark-theme");
    }
    document.body.classList.toggle("chat");

    this.localStorageService.localStorageChanged.subscribe((value: string) => {
      this.mode = value === 'Dark' ? 'Dark' : 'Light';
      this.backgroundImage = 'url(/api/swirl/sapi/branding/?target=' + this.mode.toLowerCase() + ')';
    });

    // Check if there is a chat_id param and load messages
    this.route.queryParams.subscribe(params => {
      this.chatId = params['chat_id'] || null;
      if (this.chatId) {
        this.chatService.setChatId(this.chatId);

        // Initialize local messages array from ChatService
        this.messages = [...this.chatService.getMessages(this.chatId)];

        // Subscribe to changes in the ChatService messages
        this.chatService.getMessagesObservable(this.chatId).subscribe(messages => {
          this.isConversationStarted = true;
          // If local array is empty but we get fresh data, sync them
          if (messages.length === 0 && this.messages.length === 0) {
            this.messages = [...this.chatService.getMessages(this.chatId)];
          }

          // If new messages arrive, update local array
          if (this.messages.length < messages.length) {
            this.messages = [...messages];
          } else if (this.authService.getRagRatingStatus() && messages.length > 1) {
            let didUpdate = false;

            const merged = this.messages.map((localMsg, idx) => {
              const fresh = messages[idx];

              // only assistant messages carry rating flags
              if (localMsg.role === 'assistant') {
                const flags = ['rated', 'show_feedback_box', 'rating', 'show_thanks_message', 'show_other_modal'];
                const hasDiff = flags.some(key => localMsg[key] !== fresh[key]);
                if (hasDiff) {
                  didUpdate = true;
                  return fresh;
                }
              }

              return localMsg;
            });

            if (didUpdate) {
              this.messages = merged;
            }
          }
        });
        this.updateUrlWithChatId(this.chatId);
      }
    });

    // Watch for conversation start in ChatService
    this.chatService.isConversationStarted$.subscribe((isConversationStarted) => {
      this.isConversationStarted = isConversationStarted;
      this.chatId = this.chatService.getChatId();

      // Re-subscribe for messages if chatId changes
      this.chatService.getMessagesObservable(this.chatId).subscribe(messages => {
        if (messages.length === 0 && this.messages.length === 0) {
          this.messages = [...this.chatService.getMessages(this.chatId)];
        }
        if (this.messages.length < messages.length) {
          this.messages = [...messages];
        }
      });
      this.updateUrlWithChatId(this.chatId);
    });

    // Handle "erase" event (reset the chat)
    this.chatService.eraseEvent.subscribe((isErased) => {
      if (isErased) {
        this.router.navigate(['/chat']);
        this.chatService.changeEraseEvent(false);
        this.isConversationStarted = false;
        this.messages = [];
        this.chatService.clearChatId();
      }
    });
  }

  ngAfterViewInit() {
    const wasOpen = localStorage.getItem('chat-sidenav-open') === 'true';
    if (wasOpen) {
      this.historySidenav.open();
    }
  }

  toggleHistory() {
    this.historySidenav.toggle();
    localStorage.setItem('chat-sidenav-open', this.historySidenav.opened ? 'true' : 'false');
  }

  onExportPdf() {
    exportChatPdf()
  }

  ngOnDestroy(): void {
    this.cookieConsentService.destroyCookieConsent();
  }

  handleLogoClick() {
    if (this.historySidenav?.opened) {
      this.historySidenav.close();
      localStorage.setItem('chat-sidenav-open', 'false');
    }
    this.chatService.changeEraseEvent(true);
  }
}
